import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core'
import { animate, AnimationBuilder, AnimationPlayer, style } from '@angular/animations'
import { MediaObserver } from '@ngbracket/ngx-layout'
import { Subscription } from 'rxjs'

import { SidebarService } from './sidebar.service'
import { MatchMediaService } from '@shared/services/match-media.service'

@Component({
  selector: 'lin-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() align: 'left' | 'right'

  @HostBinding('class.open') opened: boolean

  @Input() lockedOpen: string

  @HostBinding('class.locked-open') isLockedOpen: boolean

  @HostBinding('class.folded')
  @Input()
  set folded(value: boolean) {
    // Only work if the sidebar is not closed
    if (!this.opened) {
      return
    }

    // Set the folded
    this._folded = value

    // Programmatically add/remove margin to the element
    // that comes after or before based on the alignment
    let sibling, styleRule

    const styleValue = '74px'

    // Get the sibling and set the style rule
    if (this.align === 'left') {
      sibling = this.elementRef.nativeElement.nextElementSibling
      styleRule = 'marginLeft'
    } else {
      sibling = this.elementRef.nativeElement.previousElementSibling
      styleRule = 'marginRight'
    }

    // If there is no sibling, return...
    if (!sibling) {
      return
    }

    // If folded...
    if (value) {
      // Set the style
      this.renderer.setStyle(sibling, styleRule, styleValue)
    } else {
      // Remove the style
      this.renderer.removeStyle(sibling, styleRule)
    }
  }

  get folded(): boolean {
    return this._folded
  }

  // Folded unfolded
  @HostBinding('class.unfolded')
  unfolded: boolean

  // Private
  private _folded: boolean
  private _wasActive: boolean
  private _backdrop: HTMLElement | null = null
  private _player: AnimationPlayer
  private _onMediaChangeSubscription: Subscription

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private animationBuilder: AnimationBuilder,
    private mediaObserver: MediaObserver,
    private sidebarService: SidebarService,
    private matchMediaService: MatchMediaService,
  ) {
    this.opened = false
    this.folded = false
    this.align = 'left'
  }

  ngOnInit(): void {
    this.sidebarService.register(this)

    this._setupAlignment()

    this._setupLockedOpen()
  }

  ngOnDestroy(): void {
    // If the sidebar is folded, unfold it to revert modifications
    if (this.folded) {
      this.unfold()
    }

    this.sidebarService.unregister()
    this._onMediaChangeSubscription.unsubscribe()
  }

  private _setupAlignment(): void {
    if (this.align === 'right') {
      this.renderer.addClass(this.elementRef.nativeElement, 'right-aligned')
    } else {
      this.renderer.addClass(this.elementRef.nativeElement, 'left-aligned')
    }
  }

  private _setupLockedOpen(): void {
    if (!this.lockedOpen) {
      return
    }

    // Set the wasActive for the first time
    this._wasActive = false

    // Act on every media change
    this._onMediaChangeSubscription = this.matchMediaService.onMediaChange.subscribe(() => {
      // Get the active status
      const isActive = this.mediaObserver.isActive(this.lockedOpen)

      // If the both status are the same, don't act
      if (this._wasActive === isActive) {
        return
      }

      // Activate the lockedOpen
      if (isActive) {
        // Set the lockedOpen status
        this.isLockedOpen = true

        // Force the the opened status to true
        this.opened = true

        this.fold()

        // Hide the backdrop if any exists
        this.hideBackdrop()
      } else {
        // Set the lockedOpen status
        this.isLockedOpen = false

        // Unfold the sidebar in case if it was folded
        this.unfold()

        // Force the the opened status to close
        this.opened = false
      }

      // Store the new active status
      this._wasActive = isActive
    })
  }

  open(): void {
    if (this.opened || this.isLockedOpen) {
      return
    }

    this.showBackdrop()

    this.opened = true
  }

  close(): void {
    if (!this.opened || this.isLockedOpen) {
      return
    }

    this.hideBackdrop()

    this.opened = false
  }

  toggleOpen(): void {
    if (this.opened) {
      this.close()
    } else {
      this.open()
    }
  }

  fold(): void {
    // Only work if the sidebar is not folded
    if (this.folded) {
      return
    }

    // Fold
    this.folded = true
  }

  unfold(): void {
    // Only work if the sidebar is folded
    if (!this.folded) {
      return
    }

    this.folded = false
  }

  toggleFold(): void {
    if (this.folded) {
      this.unfold()
    } else {
      this.fold()
    }
  }

  showBackdrop(): void {
    // Create the backdrop element
    this._backdrop = this.renderer.createElement('div')
    this._backdrop.classList.add('linxio-sidebar-overlay')
    this.renderer.appendChild(this.elementRef.nativeElement.parentElement, this._backdrop)

    // Create the enter animation and attach it to the player
    this._player = this.animationBuilder.build([animate('300ms ease', style({ opacity: 1 }))]).create(this._backdrop)

    this._player.play()

    this._backdrop.addEventListener('click', () => {
      this.close()
    })
  }

  hideBackdrop(): void {
    if (!this._backdrop) {
      return
    }

    // Create the leave animation and attach it to the player
    this._player = this.animationBuilder.build([animate('300ms ease', style({ opacity: 0 }))]).create(this._backdrop)

    this._player.play()

    this._player.onDone(() => {
      if (this._backdrop) {
        // Remove the backdrop
        this._backdrop.parentNode.removeChild(this._backdrop)
        this._backdrop = null
      }
    })
  }
}
